import React, { useMemo, useState } from 'react';
import {
  Button,
  Drawer,
  Input,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Typography,
  message,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FctTarjeta, fmtFecha } from '../../fct/react';
import type { CatalogosAltas, ClienteCoincidente } from '../../api/altasCasosApi';
import { buscarCoincidentes, interpretarError } from '../../api/altasCasosApi';
import {
  AltaDirectaRequest,
  AltaDirectaResultado,
  crearClienteDirecto,
} from '../../api/clientesGridApi';

const { Text } = Typography;

interface CrearClienteDrawerProps {
  catalogos: CatalogosAltas | null;
  onCreado: (resultado: AltaDirectaResultado) => void;
}

const VACIO: AltaDirectaRequest = {
  nombre: '',
  apellido1: '',
  apellido2: '',
  email: '',
  prefijoMovil: '+34',
  movil: '',
  codigoPostal: '',
  inversor: true,
  boletin: false,
  tramosFacturacion: [],
  provinciasInteres: [],
  notasProcedencia: '',
};

/**
 * Utilidad "Crear cliente" del grid (alta directa, F1.2 §3.2): crea el cliente con su CL
 * definitivo sin pasar por la aplicación de validación — el que crea aquí ES el validador (D28).
 *
 * Campos: los del alta de §5.2 + notas de procedencia. Obligatorios MÍNIMOS a propósito
 * (briefing §3): quien usa esta pantalla está resolviendo una incidencia, no rellenando una
 * ficha comercial. El backend impone lo mismo; el SP, el borde real.
 *
 * Check de duplicado previo (criterio F1.2): consulta los coincidentes por email, móvil y
 * nombre antes de crear y los muestra en un `Modal.confirm`. **Avisa, no bloquea** — el gestor
 * decide si continúa.
 *
 * Doble ejecución (D-GC-58 / R-GC-08): el alta no es idempotente, así que la única defensa es
 * de UI — el botón queda bloqueado mientras la petición está en vuelo y toda creación pasa por
 * confirmación.
 */
export const CrearClienteDrawer: React.FC<CrearClienteDrawerProps> = ({ catalogos, onCreado }) => {
  const [abierto, setAbierto] = useState(false);
  const [datos, setDatos] = useState<AltaDirectaRequest>(VACIO);
  const [enVuelo, setEnVuelo] = useState(false);
  const [comprobando, setComprobando] = useState(false);

  const patch = (p: Partial<AltaDirectaRequest>) => setDatos((prev) => ({ ...prev, ...p }));

  const opciones = useMemo(() => ({
    tratamientos: (catalogos?.tratamientos ?? []).map((t) => ({ label: t.descripcion, value: t.id })),
    plazos: (catalogos?.plazosCompra ?? []).map((p) => ({ label: p.descripcion, value: p.id })),
    tramos: (catalogos?.tramosFacturacion ?? []).map((t) => ({
      label: t.codigo ? `${t.codigo} — ${t.descripcion}` : t.descripcion,
      value: t.id,
    })),
    provincias: (catalogos?.provincias ?? []).map((p) => ({ label: p.descripcion, value: p.id })),
  }), [catalogos]);

  // Plazo → fecha límite calculada, no editable (spec §6.2, regla GDeco §3.2). Se muestra
  // para que el gestor vea qué se va a escribir; la calcula el backend, no viaja en el request.
  const fechaLimite = useMemo(() => {
    switch (datos.idPlazoCompra) {
      case 1: return dayjs().add(1, 'year');
      case 2: return dayjs().add(2, 'year');
      case 3: return dayjs().add(3, 'year');
      default: return null;
    }
  }, [datos.idPlazoCompra]);

  const cerrar = () => {
    if (enVuelo) return;
    setDatos(VACIO);
    setAbierto(false);
  };

  const ejecutarAlta = async () => {
    setEnVuelo(true);
    try {
      const resultado = await crearClienteDirecto({
        ...datos,
        nombre: datos.nombre.trim(),
        // Sin ficha de compra si no es inversor: que el formulario recuerde lo tecleado no
        // significa enviarlo.
        idPlazoCompra: datos.inversor ? datos.idPlazoCompra : undefined,
        tramosFacturacion: datos.inversor ? datos.tramosFacturacion : [],
        provinciasInteres: datos.inversor ? datos.provinciasInteres : [],
      });

      message.success(
        `Cliente ${resultado.codigoCl} creado. Queda registrado en el listado de gestión como "Alta directa".`,
      );
      // `resultado.geolocalizacionResuelta` NO se avisa: mientras Q-GC-11 (fuente CP→lat/lon) siga
      // abierta, el adaptador devuelve siempre "pendiente", así que el aviso saltaría en cada alta
      // y enseñaría a ignorar los avisos. Es un estado conocido de TODAS las altas, no una
      // incidencia de esta. Cuando exista la fuente real, entonces sí merecerá aviso.
      onCreado(resultado);
      setDatos(VACIO);
      setAbierto(false);
    } catch (error) {
      const { mensaje, faltas } = interpretarError(error);
      if (faltas?.length) {
        Modal.warning({ title: mensaje, content: <ul>{faltas.map((f) => <li key={f}>{f}</li>)}</ul> });
      } else {
        message.error(mensaje);
      }
    } finally {
      setEnVuelo(false);
    }
  };

  const confirmarAlta = (coincidencias: ClienteCoincidente[]) => {
    const nombreCompleto = `${datos.nombre} ${datos.apellido1 ?? ''} ${datos.apellido2 ?? ''}`.trim();

    Modal.confirm({
      title: coincidencias.length > 0
        ? `Hay ${coincidencias.length} posible${coincidencias.length > 1 ? 's' : ''} coincidencia${coincidencias.length > 1 ? 's' : ''} en la base`
        : 'Crear el cliente',
      width: 560,
      okText: coincidencias.length > 0 ? 'Crear de todos modos' : 'Crear cliente',
      cancelText: 'Cancelar',
      content: (
        <div>
          {coincidencias.length > 0 ? (
            <>
              <Text>
                ¡Cuidado! puede ser un duplicado.  Asegúrate de que {nombreCompleto} es un
                cliente nuevo. Posible coincidencia:
              </Text>
              <ul>
                {coincidencias.map((c) => (
                  <li key={c.idCliente}>
                    {c.idCodigoGf ?? c.idCliente} — {c.nombre} {c.apellido1} {c.apellido2}
                    {c.email ? ` (${c.email})` : ''}
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <Text>
              No se han encontrado coincidencias. Se creará {nombreCompleto} con código CL definitivo,
              sin pasar por la validación.
            </Text>
          )}
        </div>
      ),
      onOk: ejecutarAlta,
    });
  };

  const handleCrear = async () => {
    if (!datos.nombre.trim()) {
      message.error('El nombre es obligatorio.');
      return;
    }

    // Check de duplicado previo. Si la consulta falla, NO se corta el alta: es un aviso, no un
    // requisito (§3.2) — se avisa de que no se pudo comprobar y se sigue a la confirmación.
    setComprobando(true);
    let coincidencias: ClienteCoincidente[] = [];
    try {
      const criterios = {
        email: datos.email?.trim() || undefined,
        movil: datos.movil?.trim() || undefined,
        nombre: `${datos.nombre} ${datos.apellido1 ?? ''}`.trim() || undefined,
      };
      const resultado = await buscarCoincidentes(criterios);
      coincidencias = resultado.candidatos;
    } catch {
      message.warning('No se ha podido comprobar si hay duplicados; revise el resultado después de crear.');
    } finally {
      setComprobando(false);
    }

    confirmarAlta(coincidencias);
  };

  return (
    <>
      <Button icon={<PlusOutlined />} onClick={() => setAbierto(true)}>
        Crear cliente
      </Button>
      <Drawer
        title="Crear cliente (alta directa)"
        width={520}
        open={abierto}
        onClose={cerrar}
        maskClosable={!enVuelo}
        extra={
          <Space>
            <Button onClick={cerrar} disabled={enVuelo}>Cancelar</Button>
            <Button
              type="primary"
              onClick={handleCrear}
              loading={comprobando || enVuelo}
              disabled={comprobando || enVuelo}
            >
              Crear cliente
            </Button>
          </Space>
        }
      >
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <FctTarjeta titulo="Identidad">
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Select
                style={{ width: '100%' }}
                placeholder="Tratamiento (Sr./Sra.)"
                value={datos.idTratamiento ?? undefined}
                onChange={(v) => patch({ idTratamiento: v ?? null })}
                options={opciones.tratamientos}
                allowClear
              />
              <Input
                addonBefore="Nombre *"
                value={datos.nombre}
                onChange={(e) => patch({ nombre: e.target.value })}
              />
              <Input
                addonBefore="1er apellido"
                value={datos.apellido1}
                onChange={(e) => patch({ apellido1: e.target.value })}
              />
              <Input
                addonBefore="2º apellido"
                value={datos.apellido2}
                onChange={(e) => patch({ apellido2: e.target.value })}
              />
              <InputNumber
                addonBefore="Año nac."
                style={{ width: '100%' }}
                min={1900}
                max={new Date().getFullYear()}
                value={datos.anioNacimiento ?? undefined}
                onChange={(v) => patch({ anioNacimiento: v ?? null })}
              />
            </Space>
          </FctTarjeta>

          <FctTarjeta titulo="Contacto">
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Input
                addonBefore="Email"
                value={datos.email}
                onChange={(e) => patch({ email: e.target.value })}
              />
              <Space.Compact style={{ width: '100%' }}>
                <Input
                  style={{ width: 90 }}
                  value={datos.prefijoMovil}
                  onChange={(e) => patch({ prefijoMovil: e.target.value })}
                />
                <Input
                  placeholder="Móvil"
                  value={datos.movil}
                  onChange={(e) => patch({ movil: e.target.value })}
                />
              </Space.Compact>
            </Space>
          </FctTarjeta>

          <FctTarjeta titulo="Residencia">
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Select
                style={{ width: '100%' }}
                placeholder="Provincia"
                showSearch
                optionFilterProp="label"
                value={datos.idProvincia ?? undefined}
                onChange={(v) => patch({ idProvincia: v ?? null })}
                options={opciones.provincias}
                allowClear
              />
              <Input
                addonBefore="C. postal"
                value={datos.codigoPostal}
                onChange={(e) => patch({ codigoPostal: e.target.value })}
              />
            </Space>
          </FctTarjeta>

          <FctTarjeta titulo="Inversor">
            <Radio.Group
              value={datos.inversor}
              onChange={(e) => patch({ inversor: e.target.value })}
            >
              <Radio value={true}>Sí</Radio>
              <Radio value={false}>No</Radio>
            </Radio.Group>
          </FctTarjeta>

          {datos.inversor && (
            <FctTarjeta titulo="Ficha de compra">
              <Space direction="vertical" size={12} style={{ width: '100%' }}>
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Tramos de facturación (A–H)"
                  value={datos.tramosFacturacion}
                  onChange={(v) => patch({ tramosFacturacion: v })}
                  options={opciones.tramos}
                />
                <Select
                  mode="multiple"
                  style={{ width: '100%' }}
                  placeholder="Provincias de interés"
                  showSearch
                  optionFilterProp="label"
                  value={datos.provinciasInteres}
                  onChange={(v) => patch({ provinciasInteres: v })}
                  options={opciones.provincias}
                />
                <Select
                  style={{ width: '100%' }}
                  placeholder="Plazo de compra"
                  value={datos.idPlazoCompra ?? undefined}
                  onChange={(v) => patch({ idPlazoCompra: v ?? null })}
                  options={opciones.plazos}
                  allowClear
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  Fecha límite: {fechaLimite ? <span className="fct-mono">{fmtFecha(fechaLimite.toDate())}</span> : 'sin fecha'} (calculada
                  a partir del plazo, no editable)
                </Text>
              </Space>
            </FctTarjeta>
          )}

          <FctTarjeta titulo="Consentimientos">
            <Radio.Group
              value={datos.boletin}
              onChange={(e) => patch({ boletin: e.target.value })}
            >
              <Radio value={true}>Enviar boletín</Radio>
              <Radio value={false}>No enviar</Radio>
            </Radio.Group>
          </FctTarjeta>

          <FctTarjeta titulo="Notas de procedencia">
            <Input.TextArea
              rows={3}
              placeholder="Motivo del alta directa (incidencia, caso especial...)"
              value={datos.notasProcedencia}
              onChange={(e) => patch({ notasProcedencia: e.target.value })}
            />
          </FctTarjeta>
        </Space>
      </Drawer>
    </>
  );
};

export default CrearClienteDrawer;
